import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath, Link } from 'react-router-dom';

import Close from '@mui/icons-material/Close';
import { Button, IconButton, Typography } from '@mui/material';

import { Constants, getCDNOptimizedImage, ROUTES } from '@arrived/common';
import { AccountOfferingBalance } from '@arrived/models';
import { useOfferingDocumentsQuery, useOfferingQuery } from '@arrived/queries';

import { OfferingName } from '../../../OfferingName';
import { PositionDetailSecondaryTypography } from '../../../Portfolio/offering/PositionDetailSecondaryTypography';
import { OfferingDividendHistory } from '../../offering/OfferingDividendHistory';
import { StyledOfferingDialog } from './OfferingDialog.styles';

import { PropertyDetailsFinancials } from '../../../PropertyPage/PropertyDetails/financials';
import { filterPropertyFinancialDocuments } from '../../../PropertyPage/PropertyDetails/utils/filterPropertyFinancialDocuments';
import { OfferingBalance, OfferingSharePrice, OfferingTransactionList } from '../../offering';
export interface OfferingDialogProps {
  accountOfferingBalance?: AccountOfferingBalance;
  setSelectedOfferingId: (id?: number) => void;
}

export const OfferingDialog = ({ accountOfferingBalance, setSelectedOfferingId }: OfferingDialogProps) => {
  const intl = useIntl();

  const handleOnClose = useCallback(() => setSelectedOfferingId(undefined), [setSelectedOfferingId]);

  return (
    <StyledOfferingDialog open={accountOfferingBalance != null} onClose={handleOnClose}>
      {accountOfferingBalance != null && <OfferingDialogContent accountOfferingBalance={accountOfferingBalance} />}
      <IconButton
        aria-label={intl.formatMessage({ id: 'close-dialog', defaultMessage: 'Close Dialog' })}
        className="close-button"
        onClick={handleOnClose}
      >
        <Close />
      </IconButton>
    </StyledOfferingDialog>
  );
};

export interface OfferingDialogContentProps {
  accountOfferingBalance: AccountOfferingBalance;
}

const OfferingDialogContent = ({ accountOfferingBalance }: OfferingDialogContentProps) => {
  const { appreciation, id, imgUrl, name, rentalIncome, shortName, totalSharesCount, totalValue } =
    accountOfferingBalance;
  const intl = useIntl();

  const offeringState = useOfferingQuery(id, { staleTime: Infinity });

  const offeringDocumentsState = useOfferingDocumentsQuery(offeringState.data?.id);
  const financialDocuments = useMemo(
    () =>
      offeringDocumentsState.data?.length ? filterPropertyFinancialDocuments(offeringDocumentsState.data) : undefined,
    [offeringDocumentsState.data]
  );

  const imageSrc = useMemo(
    () =>
      getCDNOptimizedImage({
        imgUrl: imgUrl ?? Constants.defaultPropertyImageUrl,
        // Should be slightly larger than the maximum width which will be 50% of the width of the dialog.
        width: `600px`,
      }),
    [imgUrl]
  );

  return (
    <>
      <div className="header-content">
        <img
          alt={intl.formatMessage({ id: 'offering-image', defaultMessage: 'Image of {name}' }, { name })}
          className="header-image"
          src={imageSrc}
        />
        <div className="header-description">
          <Typography variant="h4.semibold">
            <OfferingName name={name} />
          </Typography>
          <PositionDetailSecondaryTypography>
            <OfferingSharePrice offeringId={id} />
          </PositionDetailSecondaryTypography>
          <Button
            className="property-page-button"
            component={Link}
            to={generatePath(ROUTES.properties.propertyDetails, { idOrShortName: shortName })}
            color="primary"
          >
            <FormattedMessage id="view-property-page" defaultMessage="View Property Page" />
          </Button>
        </div>
      </div>
      <div className="body-content">
        {offeringState.data && (
          <OfferingBalance
            className="offering-balance"
            appreciation={appreciation}
            equity={totalValue}
            numShares={totalSharesCount}
            offering={offeringState.data}
            rentalIncome={rentalIncome}
          />
        )}
        {financialDocuments && <PropertyDetailsFinancials financialDocuments={financialDocuments} />}
        <OfferingDividendHistory dividends={rentalIncome.allDividends} />
        <OfferingTransactionList className="offering-transaction-list" offeringId={id} />
      </div>
    </>
  );
};
